import type {
  AttachmentDto,
  ExpeditorDocumentDto,
  OrderCreateInput,
  OrderDetail,
  OrderListItem,
  OrderListQuery,
  OrderPatchInput,
  OrderRouteSegmentsPatchInput,
} from '@expeditor/shared';
import { apiClient } from './client';

export const ordersApi = {
  async list(query: OrderListQuery = {}): Promise<OrderListItem[]> {
    const params: Record<string, string> = {};
    if (query.status) params.status = query.status;
    if (query.fromDate) params.fromDate = query.fromDate;
    if (query.toDate) params.toDate = query.toDate;
    const res = await apiClient.get<OrderListItem[]>('/orders', { params });
    return res.data;
  },

  async getOne(id: string): Promise<OrderDetail> {
    const { data } = await apiClient.get<OrderDetail>(`/orders/${id}`);
    return data;
  },

  async create(dto: OrderCreateInput): Promise<OrderDetail> {
    const { data } = await apiClient.post<OrderDetail>('/orders', dto);
    return data;
  },

  async update(id: string, patch: OrderPatchInput): Promise<OrderDetail> {
    const { data } = await apiClient.patch<OrderDetail>(`/orders/${id}`, patch);
    return data;
  },

  async submit(id: string): Promise<OrderDetail> {
    const { data } = await apiClient.post<OrderDetail>(`/orders/${id}/submit`);
    return data;
  },

  async clone(id: string): Promise<OrderDetail> {
    const { data } = await apiClient.post<OrderDetail>(`/orders/${id}/clone`);
    return data;
  },

  /** XML титула 1 (информация клиента) по формату ФНС ЕД-1-26/277@. */
  async fnsTitle1(id: string): Promise<{ fileName: string; xml: string }> {
    const { data } = await apiClient.get<{ fileName: string; xml: string }>(
      `/orders/${id}/fns-title1`,
    );
    return data;
  },

  /** XML экспедиторской расписки (ЭР, ON_EXPRASP). */
  async expRasp(id: string): Promise<{ fileName: string; xml: string }> {
    const { data } = await apiClient.get<{ fileName: string; xml: string }>(
      `/orders/${id}/exp-rasp`,
    );
    return data;
  },

  /** XML титула экспедитора поручения (ON_POREXPEXP). */
  async porExpExp(id: string): Promise<{ fileName: string; xml: string }> {
    const { data } = await apiClient.get<{ fileName: string; xml: string }>(
      `/orders/${id}/porexp-exp`,
    );
    return data;
  },

  /** Печатная форма «Поручение экспедитору» (Word/.doc). */
  async printable(id: string): Promise<{ fileName: string; html: string }> {
    const { data } = await apiClient.get<{ fileName: string; html: string }>(
      `/orders/${id}/printable`,
    );
    return data;
  },

  /** XML титула грузоотправителя ЭТрН (автомобильная). */
  async etrnTitleGO(id: string): Promise<{ fileName: string; xml: string }> {
    const { data } = await apiClient.get<{ fileName: string; xml: string }>(
      `/orders/${id}/etrn-title-go`,
    );
    return data;
  },

  async remove(id: string): Promise<{ deleted: true }> {
    const { data } = await apiClient.delete<{ deleted: true }>(`/orders/${id}`);
    return data;
  },

  async uploadAttachment(
    orderId: string,
    file: File,
    category?: string,
  ): Promise<AttachmentDto> {
    const form = new FormData();
    form.append('file', file);
    if (category) form.append('category', category);
    const { data } = await apiClient.post<AttachmentDto>(
      `/orders/${orderId}/attachments`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return data;
  },

  async deleteAttachment(orderId: string, attachmentId: string): Promise<void> {
    await apiClient.delete(`/orders/${orderId}/attachments/${attachmentId}`);
  },

  downloadAttachmentUrl(attachmentId: string): string {
    return `/api/attachments/${attachmentId}/download`;
  },
};

export const managerOrdersApi = {
  async list(query: OrderListQuery = {}): Promise<OrderListItem[]> {
    const params: Record<string, string> = {};
    if (query.status) params.status = query.status;
    if (query.clientId) params.clientId = query.clientId;
    if (query.fromDate) params.fromDate = query.fromDate;
    if (query.toDate) params.toDate = query.toDate;
    const res = await apiClient.get<OrderListItem[]>('/manager/orders', { params });
    return res.data;
  },
  async getOne(id: string): Promise<OrderDetail> {
    const { data } = await apiClient.get<OrderDetail>(`/manager/orders/${id}`);
    return data;
  },
  /** Правка поручения за клиента (нужно право canActForClients). */
  async update(id: string, patch: OrderPatchInput): Promise<OrderDetail> {
    const { data } = await apiClient.patch<OrderDetail>(`/manager/orders/${id}`, patch);
    return data;
  },
  /** Печатная форма поручения (Word) — тот же бланк, что у клиента. */
  async printable(id: string): Promise<{ fileName: string; html: string }> {
    const { data } = await apiClient.get<{ fileName: string; html: string }>(
      `/manager/orders/${id}/printable`,
    );
    return data;
  },
  /** Уточнение маршрута по плечам — доступно и после подачи поручения. */
  async updateRouteSegments(
    id: string,
    patch: OrderRouteSegmentsPatchInput,
  ): Promise<OrderDetail> {
    const { data } = await apiClient.patch<OrderDetail>(
      `/manager/orders/${id}/route-segments`,
      patch,
    );
    return data;
  },
  /** Подача поручения на котировку за клиента. */
  async submit(id: string): Promise<OrderDetail> {
    const { data } = await apiClient.post<OrderDetail>(`/manager/orders/${id}/submit`);
    return data;
  },
  /** Создание поручения за клиента. */
  async createForClient(clientId: string, dto: OrderCreateInput): Promise<OrderDetail> {
    const { data } = await apiClient.post<OrderDetail>(
      `/manager/clients/${clientId}/orders`,
      dto,
    );
    return data;
  },
  /** Экспедиторские документы заказа (Фаза 2, клиент→наш экспедитор). */
  async documents(id: string): Promise<ExpeditorDocumentDto[]> {
    const { data } = await apiClient.get<ExpeditorDocumentDto[]>(
      `/manager/orders/${id}/documents`,
    );
    return data;
  },
  async documentXml(id: string, docId: string): Promise<{ fileName: string; xml: string }> {
    const { data } = await apiClient.get<{ fileName: string; xml: string }>(
      `/manager/orders/${id}/documents/${docId}/xml`,
    );
    return data;
  },
  /** Принять поручение: выпустить и сохранить титул экспедитора (POREXPEXP). */
  async acceptPoruchenie(id: string): Promise<ExpeditorDocumentDto[]> {
    const { data } = await apiClient.post<ExpeditorDocumentDto[]>(
      `/manager/orders/${id}/accept-poruchenie`,
    );
    return data;
  },
  /** Выпустить экспедиторскую расписку (EXPRASP) по заказу. */
  async issueRaspiska(id: string): Promise<ExpeditorDocumentDto[]> {
    const { data } = await apiClient.post<ExpeditorDocumentDto[]>(
      `/manager/orders/${id}/issue-raspiska`,
    );
    return data;
  },
  /** Отозвать поручение (POREXPOTZ) по заказу. */
  async revokePoruchenie(id: string): Promise<ExpeditorDocumentDto[]> {
    const { data } = await apiClient.post<ExpeditorDocumentDto[]>(
      `/manager/orders/${id}/revoke-poruchenie`,
    );
    return data;
  },
};
